import React, {useMemo, useState} from 'react';
import {useQuery} from 'react-query';
import {useHistory} from 'react-router-dom';
import {Autocomplete, Box, Chip, InputAdornment, LinearProgress, TextField, Typography} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Canvas from '../../navigation/canvas/Canvas';
import ConsumerService from '../../../apis/ConsumerService';
import OwnersService from '../../../apis/OwnersService';
import ProviderService from '../../../apis/ProviderService';
import DataStreamService from '../data-streams/DataStreamService';
import Search from './search';

// Every list the search covers, on the query keys the rest of the app already uses: landing
// here warms the cache the list pages read, rather than duplicating their fetches.
const useSearchData = () => {
  const owners = useQuery('getOwners', OwnersService.getAllSortedByName, {staleTime: Infinity});
  const providers = useQuery('getProviders', ProviderService.getAllSortedByName, {staleTime: Infinity});
  const dataStreams = useQuery('getDataStreams', DataStreamService.getAll, {staleTime: Infinity});
  const consumers = useQuery('getConsumers', ConsumerService.getAll, {staleTime: Infinity});

  const index = useMemo(
    () => Search.buildIndex({
      owner: owners.data,
      provider: providers.data,
      data_stream: dataStreams.data,
      consumer: consumers.data,
    }),
    [owners.data, providers.data, dataStreams.data, consumers.data]
  );

  // A list that failed is named rather than silently missing from the results.
  const unavailable = [
    ['owners', owners], ['providers', providers], ['data streams', dataStreams], ['consumers', consumers],
  ].filter(([, query]) => query.isError).map(([name]) => name);

  const loading = [owners, providers, dataStreams, consumers].some((query) => query.isLoading);

  return {index, loading, unavailable};
};

const TYPE_COLORS = {
  owner: 'primary',
  provider: 'secondary',
  data_stream: 'default',
  consumer: 'default',
};

const Home = () => {
  const history = useHistory();
  const {index, loading, unavailable} = useSearchData();
  const [query, setQuery] = useState('');

  const results = useMemo(() => Search.search(index, query), [index, query]);

  const open = (entry) => {
    if (entry) history.push(Search.linkTo(entry));
  };

  return (
    <Canvas appName='nGEST' title='Home'>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '55vh',
        px: 2,
      }}>
        <Typography variant='h4' sx={{mb: 1}}>What are you looking for?</Typography>
        <Typography variant='body2' color='textSecondary' sx={{mb: 3, textAlign: 'center'}}>
          Search owners, providers, data streams and consumers by name or id. Pick a result to
          open it.
        </Typography>

        <Box sx={{width: '100%', maxWidth: 720}}>
          <Autocomplete
            options={results}
            // Ranking and matching are done in Search.search; MUI must not filter again.
            filterOptions={(options) => options}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, selected) =>
              option.type === selected.type && option.id === selected.id}
            inputValue={query}
            onInputChange={(event, next) => setQuery(next)}
            // Kept unselected: a hit is a destination, not a value the box should hold on to.
            value={null}
            onChange={(event, option) => open(option)}
            autoHighlight
            openOnFocus
            clearOnBlur={false}
            noOptionsText={loading ? 'Loading…' : query.trim() ? 'No matches' : 'Start typing'}
            renderOption={(props, option) => (
              <Box component='li' {...props} key={`${option.type}-${option.id}`}
                   sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Chip
                  size='small'
                  variant='outlined'
                  color={TYPE_COLORS[option.type]}
                  label={option.typeLabel.replace(/s$/, '')}
                  sx={{minWidth: 96}}
                />
                <Box sx={{minWidth: 0, flex: 1}}>
                  <Typography variant='body2' noWrap>
                    {option.name} ({option.id})
                    {option.disabled && (
                      <Typography component='span' variant='caption' color='textSecondary'>
                        {' '}— disabled
                      </Typography>
                    )}
                  </Typography>
                  {option.subtitle && (
                    <Typography variant='caption' color='textSecondary' noWrap
                                sx={{display: 'block'}}>
                      {option.subtitle}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                placeholder='Search by name or id…'
                variant='outlined'
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon color='action'/>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
          {loading && <LinearProgress sx={{mt: 1}}/>}
          {unavailable.length > 0 && (
            <Typography variant='caption' color='error' sx={{mt: 1, display: 'block'}}>
              Could not load {unavailable.join(', ')}, so nothing from{' '}
              {unavailable.length > 1 ? 'those lists' : 'that list'} will appear here.
            </Typography>
          )}
        </Box>
      </Box>
    </Canvas>
  );
};

export default Home;
